import { useEffect, useState } from "react";
import { Cookie, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  ConsentPreferences,
  defaultConsent,
  hasConsentDecision,
  loadConsent,
  saveConsent,
} from "@/lib/cookieConsent";

type PreferenceKey = keyof ConsentPreferences;

const preferenceCopy: Record<
  PreferenceKey,
  { label: string; description: string; disabled?: boolean }
> = {
  necessary: {
    label: "Necessary",
    description:
      "Keeps your session data, for example selected professionals. Always enabled.",
    disabled: true,
  },
  analytics: {
    label: "Analytics",
    description:
      "Enable analytics tools (e.g., Google Analytics) if added in the future.",
  },
  marketing: {
    label: "Marketing",
    description:
      "Allow personalization/remarketing if we add these tools later.",
  },
};

const CookieConsent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(true);
  const [preferences, setPreferences] =
    useState<ConsentPreferences>(defaultConsent);

  useEffect(() => {
    if (hasConsentDecision()) {
      setPreferences(loadConsent());
      setIsVisible(false);
      return;
    }

    setIsVisible(true);
    setIsOpen(true);
  }, []);

  const closeBanner = () => {
    setIsOpen(false);
    setTimeout(() => setIsVisible(false), 400);
  };

  const handleAcceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
    setPreferences({ ...defaultConsent, analytics: true, marketing: true });
    closeBanner();
  };

  const handleRejectNonEssential = () => {
    saveConsent({ analytics: false, marketing: false });
    setPreferences({ ...defaultConsent, analytics: false, marketing: false });
    closeBanner();
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    closeBanner();
  };

  const togglePreference = (key: PreferenceKey, value: boolean) => {
    if (key === "necessary") return;
    setPreferences((prev) => ({ ...prev, [key]: value }));
  };

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 sm:left-4 sm:bottom-4 sm:max-w-xl z-50 transition-all duration-500",
        isOpen ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
      )}
    >
      <Card className="m-3 shadow-elevated border-border/70">
        <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-3 pb-4">
          <div className="space-y-1">
            <CardTitle className="text-lg flex items-center gap-2 text-brand-charcoal">
              <Cookie className="h-5 w-5 text-brand-navy" />
              Cookie preferences
            </CardTitle>
            <CardDescription className="text-sm text-brand-charcoal/80">
              We use necessary cookies to keep your cart and session working.
              Choose what else you allow.
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-1 text-brand-navy"
            onClick={() => setShowSettings((prev) => !prev)}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {showSettings ? "Hide" : "Configure"}
          </Button>
        </CardHeader>

        <CardContent className="space-y-4">
          {showSettings ? (
            <div className="space-y-3">
              {(Object.keys(preferences) as PreferenceKey[]).map((key) => (
                <div
                  key={key}
                  className={cn(
                    "flex items-start justify-between gap-3 rounded-lg border border-muted px-3 py-3",
                    preferenceCopy[key].disabled && "bg-muted/60"
                  )}
                >
                  <div className="space-y-1 pr-2">
                    <Label className="text-sm text-brand-charcoal">
                      {preferenceCopy[key].label}
                    </Label>
                    <p className="text-xs text-brand-charcoal/70 leading-relaxed">
                      {preferenceCopy[key].description}
                    </p>
                  </div>
                  <Switch
                    checked={preferences[key]}
                    disabled={preferenceCopy[key].disabled}
                    onCheckedChange={(val) => togglePreference(key, val)}
                    aria-label={`Toggle ${preferenceCopy[key].label}`}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2 text-sm text-brand-charcoal/80">
              <p className="font-medium text-brand-charcoal">
                How we use cookies at NearSmarter:
              </p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>
                  <strong>local_cart</strong>: stores selected items and quantities.
                </li>
                <li>
                  <strong>local_project_details</strong>: saves your briefing draft before submitting.
                </li>
                <li>
                  <strong>order_id</strong> and <strong>cart_id</strong>: connect your cart to the logged-in account.
                </li>
                <li>
                  Supabase session cookie to keep you signed in.
                </li>
              </ul>
              <p className="text-xs text-brand-charcoal/70">
                Turn optional categories on/off in the settings tab.
              </p>
            </div>
          )}

          <p className="text-xs text-brand-charcoal/70">
            See details in{" "}
            <a
              className="underline underline-offset-4 text-brand-navy font-semibold"
              href="/terms-of-service"
            >
              Terms of Service
            </a>
            . Reset preferences anytime by clearing the ns_cookie_consent cookie in your browser.
          </p>
        </CardContent>

        <CardFooter className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          <Button
            variant="secondary"
            className="w-full sm:w-auto"
            onClick={handleRejectNonEssential}
          >
            Reject optional
          </Button>
          <Button
            variant="secondary"
            className="w-full sm:w-auto"
            onClick={handleSavePreferences}
          >
            Save preferences
          </Button>
          <Button className="w-full sm:w-auto" onClick={handleAcceptAll}>
            Accept all
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default CookieConsent;
